import React, { useState, useEffect } from 'react';
import { useEvent } from '../../context/EventContext';
import enhancedApiClient from '../../api/enhancedApiClient';
import ModalWrapper from '../ModalWrapper';

const AddChecklistItemModal = ({ isOpen, onClose, onItemAdded }) => {
  const { eventData, reload } = useEvent();
  const [formData, setFormData] = useState({
    name: '',
    details: '',
    dueDate: '',
    documentFile: null
  });
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let documentUrl = null;
      
      if (formData.documentFile) {
        const uploadResult = await enhancedApiClient.uploadFile(formData.documentFile);
        documentUrl = uploadResult.fileUrl;
      }
      
      const taskData = {
        eventId: eventData?.eventId,
        name: formData.name,
        details: formData.details,
        dueDate: formData.dueDate,
        documentUrl: documentUrl || '',
        status: 'pending',
        createdByUserId: currentUser?.username,
        vendorId: '', // Client tasks don't have a specific vendor
        createdAt: new Date().toISOString()
      };
      
      console.log('Creating task with data:', taskData);
      const result = await enhancedApiClient.createTask(taskData);
      console.log('Task creation result:', result);
      
      setFormData({ name: '', details: '', dueDate: '', documentFile: null });
      setUploadError('');
      console.log('Reloading event data...');
      await reload(); // Reload all event data including tasks
      onItemAdded?.();
      onClose();
    } catch (error) {
      console.error('Error creating task:', error);
      alert('❌ Failed to add item. Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} maxWidth="500px">
      <h2 style={{
        fontSize: '24px',
        fontWeight: '600',
        color: '#1F2937',
        marginBottom: '20px',
        fontFamily: 'Georgia, serif'
      }}>Add Checklist Item</h2>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>Item Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              placeholder="e.g., Book photographer"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: '#F9FAFB',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#6B9080'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>Due Date</label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: '#F9FAFB',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#6B9080'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>Details <span style={{ color: '#9CA3AF' }}>(Optional)</span></label>
            <textarea
              value={formData.details}
              onChange={(e) => setFormData({...formData, details: e.target.value})}
              rows={3}
              placeholder="Add any additional details or notes..."
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: '#F9FAFB',
                resize: 'vertical',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#6B9080'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>Upload Document <span style={{ color: '#9CA3AF' }}>(Optional)</span></label>
            <div style={{ position: 'relative' }}>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    if (file.size > 10 * 1024 * 1024) {
                      setUploadError('File size must be less than 10MB');
                      return;
                    }
                    setUploadError('');
                    setFormData({...formData, documentFile: file});
                  }
                }}
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer'
                }}
                id="doc-upload"
              />
              <label
                htmlFor="doc-upload"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: '#F9FAFB',
                  cursor: 'pointer',
                  color: '#6B7280'
                }}
              >
                <svg style={{ width: '20px', height: '20px', marginRight: '8px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {formData.documentFile ? formData.documentFile.name : 'Choose file (PDF, DOC, DOCX, JPG, PNG)'}
              </label>
            </div>
            {uploadError && (
              <div style={{ color: '#DC2626', fontSize: '14px', marginTop: '4px' }}>
                {uploadError}
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 24px',
                backgroundColor: 'white',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#9CA3AF' : '#6B9080',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Adding...' : 'Add Item'}
            </button>
          </div>
        </form>
    </ModalWrapper>
  );
};

export default AddChecklistItemModal;